'use client';

/**
 * Reusable hover tooltip built on Radix (already a dependency). Wrap any
 * element with <Tooltip content={…}>…</Tooltip> to show hover information.
 * Rich content (JSX) is supported for cards like the investor context tooltip.
 * Promoted from the admin StatusTooltip pattern for use across the dashboard
 * and marketing pages.
 */

import React from 'react';
import { Tooltip as RadixTooltip } from 'radix-ui';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  delayDuration?: number;
  /** Set false for rich cards where the dark bubble style is unwanted */
  dark?: boolean;
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayDuration = 200,
  dark = true,
  className = '',
}: TooltipProps) {
  if (content == null || content === '') return <>{children}</>;

  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={12}
            className={
              dark
                ? `z-[60] max-w-[260px] overflow-hidden rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-xl animate-in fade-in-0 zoom-in-95 ${className}`
                : `z-[60] max-w-[280px] overflow-hidden rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-xl animate-in fade-in-0 zoom-in-95 ${className}`
            }
          >
            {content}
            <RadixTooltip.Arrow className={dark ? 'fill-gray-900' : 'fill-white'} />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
